import { ref } from 'vue';
import { useToast } from '@fareharbor-com/beacon-vue';
import { defineStore } from 'pinia';
import { getUser } from '@/common/api/getUser';
import { transformUserData } from '@/common/api/transformUserData';
import { handleThrownError } from '@/common/errors/handleThrownError';
import { useAuthStore } from '@/stores/auth';
import type { MarketplaceUser } from '@/types';

export const useUserStore = defineStore('user', () => {
  const user = ref<MarketplaceUser | null>(null);

  const { showToast } = useToast();
  const authStore = useAuthStore();

  function clearUserData(): void {
    user.value = null;
  }

  async function fetchUserData(): Promise<void> {
    try {
      const response = await getUser();

      if (response.status === 401 || response.status === 403) {
        authStore.logout();
        clearUserData();
        return;
      }

      if (response.status !== 200) {
        showToast('Failed to fetch user data. Please try again.', {
          variant: 'error',
        });
        return;
      }

      if (!response.data) {
        showToast(
          'Oops! Something went wrong while fetching your profile. ' +
            'Please log out and log back in, or reach out to our support team for assistance.',
          { variant: 'error' },
        );
        return;
      }

      user.value = await transformUserData(response.data);
    } catch (error) {
      handleThrownError({
        error,
        toastMessage: 'An error occurred while fetching user data.',
        log: true,
      });
    }
  }

  return {
    user,
    clearUserData,
    fetchUserData,
  };
});
